import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ErrorMessage } from '@hookform/error-message';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { isNumber } from 'lodash';
import cx from 'classnames';

import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import SearchInput from '../../../../components/modules/SearchInput';
import RolePlate from '../../../../components/modules/RolePlate';
import { displayRole, isSafari } from '../../../../helpers';
import CrossIcon from '../../../../assets/icons/cross.svg';
import Avatar from '../../../../components/modules/Avatar';
import Modal from '../../../../components/modules/Modal';
import styles from './RequestHelpModal.module.scss';
import Search from '../../../../components/modules/Search';
import { getAllUsers, getMyUserInfo, postRequestHelp } from '../../../../api';
import { RolesLowerCase, USER } from '../../../../enums';
import useDebounce from '../../../../helpers/useDebounce';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { saveUser } from '../../../../store/user/userSlice';

type TLoginModal = {
  open: boolean;
  switchModal: () => void;
};

const RequestHelpModal: React.FC<TLoginModal> = ({ open, switchModal }) => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const onClose = () => {
    setLoading(false);
    switchModal();
    setSearchText('');
    setSelectedAdmins([]);
    setHelpText('');
  };

  const [searchText, setSearchText] = useState('');
  const debouncedValue = useDebounce<string>(searchText, 500);
  const [admins, setAdmins] = useState<USER[]>([]);
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value);
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await getAllUsers(0, 300, searchText, 'asc', 'Super Admin', 'username');
      if (res?.data?.documents) {
        const filteredUsers = res.data.documents.filter((u: any) => u.email !== currentUser?.email);
        setAdmins(filteredUsers);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAdmins();
    }
  }, [open, debouncedValue]);

  const [helpText, setHelpText] = useState('');
  const [selectedAdmins, setSelectedAdmins] = useState<USER[]>([]);

  const onHelpTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setHelpText(e.target.value);

  const onAdminSelect = (admin: USER) => {
    const user = selectedAdmins.find((u) => u.id == admin.id);
    if (user) {
      setSelectedAdmins([...selectedAdmins.filter((u) => u.id != user.id)]);
    } else {
      setSelectedAdmins([...selectedAdmins, admin]);
    }
  };

  const onSubmit = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const requestedAdmins = selectedAdmins.map((u) => ({ id: u.id }));
      const transformedHelpText = `${helpText} (${myOrganization.name})`;
      await postRequestHelp({ admins: requestedAdmins, requestHelpMessage: transformedHelpText });
      notification.showMessage('Help is requested');
      const userJson = await getMyUserInfo();
      if (userJson?.data) {
        dispatch(saveUser(userJson.data));
      }
      onClose();
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? onClose : () => {}} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Request Support</div>
          <img src={CrossIcon} alt="close modal" onClick={onClose} className={styles.closeIcon} />
        </div>
        <div className={styles.modalSubTitle}>Please choose Super Admin to ask for help.</div>

        <SearchInput onChange={onSearchChange} className={styles.search} />

        <div className={cx(styles.list, 'CustomScrollbar')}>
          {admins.map((admin) => (
            <div
              key={admin.id}
              onClick={() => onAdminSelect(admin)}
              className={cx(styles.userLine, selectedAdmins.find((u) => u.id === admin.id) && styles.selected)}
            >
              <div className={styles.userInfo}>
                <Avatar avatar={admin?.avatar_url} name={admin.email} />
                <div className={styles.info}>
                  <div className={styles.name}>{admin?.username}</div>
                  {canViewEmails && (<div className={styles.email}>{admin?.email}</div>)}
                </div>
              </div>
              <div className={styles.role}>
                <RolePlate userRole={displayRole(RolesLowerCase.SUPER_ADMIN)} />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.descriptionLabel}>Here you can text us on what is the problem:</div>
        <div className={styles.descriptionBg}>
          <textarea
            onChange={onHelpTextChange}
            className={styles.descriptionField}
            placeholder="Type your question here..."
            style={{ resize: 'vertical', overflow: 'hidden' }}
            id="description"
            cols={33}
          />
        </div>

        <div className={styles.saveBtnContainer}>
          <ContainedButton onClick={onSubmit} disabled={!selectedAdmins.length || !helpText.length}>
            Request support
          </ContainedButton>
          <OutlinedButton kind="default" onClick={onClose}>
            No, cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default RequestHelpModal;
